const _ = require("lodash");
const PS = require("./ParserState");
const DelimParser = require("./Delim");
const DiagramColors = require("../../util/ColorUtil").getDiagramColors();

// Have the parser eat all white space at the current position. Returns the
// string of whitespace consumed. Options available:
// - newlineLimit: Number of newlines to allow. Stops right before the (k+1)th
//   newline if this value is equal to k. No limit if negative or undefined.
const parseAndIgnoreSpace = function(ps, {newlineLimit} = {}) {
	ps = PS.convert(ps);
	const origPos = ps.pos;
	let newlinesLeft =
		newlineLimit === undefined ? -1 : Math.floor(+newlineLimit);
	while (/\s/.test(ps.text[ps.pos])) {
		if (ps.text[ps.pos] === "\n" && newlinesLeft === 0) {
			break;
		} else if (ps.text[ps.pos] === "\n" && newlinesLeft > 0) {
			newlinesLeft -= 1;
		}
		ps.pos += 1;
	}
	return ps.text.slice(origPos, ps.pos);
};

// Check for all occurrences of four consecutive digits without a comma between
// them, which throws an error. The parser state is unaltered.
const checkForFourDigits = function(ps, text) {
	let pos = ps.pos;
	const pattern = /([^#\dA-Za-z\.]|^)\d{4,}/;
	let result;
	let errorPos;
	while (text.length) {
		result = pattern.exec(text);
		if (!result) {
			break;
		}
		errorPos = pos + result.index + result[1].length;
		ps.pushError("Four digits in a row without a comma separator.", errorPos);
		pos += result.index + result[0].length;
		text = text.slice(result.index + result[0].length);
	}
};

// Parse an argument for a command a given number of times. count is not
// optional.
// The argument is either a single character or one surrounded in braces.
// Available options:
// - noSpace: set to true to not allow whitespace before the argument.
// - requireBrace: set to true to prevent unbraced single character arguments.
// Returns false if an error prevented finding the requested number of
// arguments. Else, returns an array with length count, each containing a
// parser state object.
const findArguments = function(ps, count, options = {}) {
	const {noSpace, requireBrace} = options;
	ps = PS.convert(ps);
	if (count <= 0) {
		return [];
	}

	let firstChar = ps.text[ps.pos];
	const origPos = ps.pos;

	// Mimic LaTeX: eat all whitespace leading up to an argument.
	while (!noSpace && firstChar && /\s/.test(firstChar)) {
		ps.pos += 1;
		firstChar = ps.text[ps.pos];
	}
	if (!firstChar) {
		ps.pushError("Input ended while parsing arguments.");
		return false;
	}

	let argStartPos;
	let argEndPos;
	if (requireBrace && firstChar !== "{") {
		ps.pushError("Arguments for this command must be inside braces.", origPos);
		return false;
	} else if (firstChar === "\\") {
		ps.pushError("Implicitly braced argument '\\' is invalid.");
		ps.pos += 1;
		return false;
	} else if (firstChar !== "{") {
		argStartPos = ps.pos;
		argEndPos = ps.pos + 1;
		ps.pos = argEndPos;
	} else {
		argStartPos = ps.pos + 1;
		argEndPos = findNestedDelim(
			ps.at(argStartPos),
			{
				begin: "{",
				escape: "\\",
			},
			{
				begin: "}",
				escape: "\\",
			}
		);
		if (argEndPos === -1) {
			ps.pushError("Unmatched '{' beginning an argument.");
			ps.pos += 1;
			return false;
		}
		ps.pos = argEndPos + 1;
	}

	const argumentText = ps.text.slice(argStartPos, argEndPos);
	const argumentPs = ps.withText(argumentText).pipeErrorsAt(argStartPos);
	const restOfPs = findArguments(ps, count - 1, options);
	return restOfPs && [argumentPs].concat(restOfPs);
};

// Parse a variable number of arguments for a command until an invalid one is
// found, which may result in 0 arguments. Arguments must be braced.
// Available options:
// - noSpace: set to true to not allow whitespace before the argument.
// Returns an array of parser state objects, one for each found argument.
const findSomeArguments = function(ps, {noSpace} = {}) {
	const options = {noSpace, requireBrace: true};
	const foundArgs = [];
	while (true) {
		const tryPs = ps.clone().setIgnoreErrors(true);
		const oneArg = findArguments(tryPs, 1, options);
		if (!oneArg) {
			break;
		}
		foundArgs.push(oneArg[0]);
		ps.pos = tryPs.pos;
	}
	return foundArgs;
};

// Parse an optional argument for a command which is inside square brackets.
// The parser state should come before where the square bracket is expected.
// Available options:
// - noSpace: set to true to not allow whitespace before the argument.
// If an optional argument is found, the parser state is moved past it and
// a new parser state containing the argument is returned.
// If an optional argument is not found, the parser state is left unchanged, and
// false is returned.
// An error is only produced if a [ is found without a matching ].
const findOptionalArgument = function(ps, {noSpace} = {}) {
	ps = PS.convert(ps);

	const origPos = ps.pos;
	let firstChar = ps.text[ps.pos];
	// Mimic LaTeX: eat all whitespace leading up to an argument.
	while (!noSpace && firstChar && /\s/.test(firstChar)) {
		ps.pos += 1;
		firstChar = ps.text[ps.pos];
	}
	if (firstChar !== "[") {
		ps.pos = origPos;
		return false;
	}

	const argStartPos = ps.pos + 1;
	const argEndPos = findDelim(ps.at(argStartPos), {
		begin: "]",
		escape: "\\",
	});
	if (argEndPos === -1) {
		ps.pushError("Unmatched '[' beginning an optional argument.");
		ps.pos = origPos;
		return false;
	}

	const argumentText = ps.text.slice(argStartPos, argEndPos);
	const argumentPs = ps.withText(argumentText).pipeErrorsAt(argStartPos);
	ps.pos = argEndPos + 1;
	return argumentPs;
};

// Uses findOptionalArgument to find all optional arguments, and returns an
// array of the results that can be checked for given values.
const findOptionalArguments = function(ps, {noSpace} = {}) {
	const args = [];
	let keepLooking = true;
	while (keepLooking) {
		const arg = findOptionalArgument(ps, {noSpace});
		if (arg === false) {
			keepLooking = false;
		} else {
			args.push(arg.getRest());
		}
	}
	return args;
};

// Convenience function used in a lot of commands. Take a parser state with
// a semicolon separated list and return an array of text
const splitArgIntoText = function(ps) {
	ps = PS.convert(ps);
	const text = ps.getRest();
	return text ? splitByDelimIntoPs(ps, {begin: ";"}) : [];
};

// Convenience function used in a lot of commands. Take a parser state with
// a semicolon separated list of numbers and return the numbers as an array
// while handling edge cases.
const splitArgIntoNumbers = function(ps) {
	ps = PS.convert(ps);
	const text = ps.getRest();
	return text ? text.split(";").map(parseCommaNumber) : [];
};

// Convenience function used in a lot of commands. Take a parser state with
// a semicolon separated list of math and return a list of nodes after sending
// the contents through parseMath.
const splitArgIntoMathNodes = function(ps) {
	ps = PS.convert(ps);
	const text = ps.getRest();
	return text ? splitByDelimIntoPs(ps, {begin: ";"}).map(makeMathArgNode) : [];
};

// Convenience function used in a lot of commands. Take a parser state with
// a semicolon separated list of nonmath and return a list of nodes after sending
// the contents through parseMath.
const splitArgIntoNonmathNodes = function(ps) {
	ps = PS.convert(ps);
	const text = ps.getRest();
	return text
		? splitByDelimIntoPs(ps, {begin: ";"}).map(makeNonmathArgNode)
		: [];
};

// This function parses a block of math, with the parser state at the
// beginning delimiter and the end of the math yet to be found.
// type is the type of math node to wrap the results in.
// Delegates to parseMath after finding all the math.
const parseStartOfMath = function(ps, type, beginDelim, endDelim) {
	ps = PS.convert(ps);
	const beginPos = ps.pos + beginDelim.length;
	ps.pos = beginPos;

	const endPos = findDelim(ps, {
		begin: endDelim,
		escape: "\\",
	});
	if (endPos === -1) {
		ps.pushError(
			"Math delimiter " +
				beginDelim +
				" did not have matching end delimiter " +
				endDelim
		);
		return [
			{
				type: "error",
				name: beginDelim,
			},
		].concat(ps.parseNonmath(ps));
	}

	const psForMath = ps
		.withText(ps.text.slice(beginPos, endPos))
		.pipeErrorsAt(beginPos);
	ps.pos = endPos + endDelim.length;
	return [
		{
			type: type,
			children: ps.parseMath(psForMath),
		},
	];
};

// Convenience function for command handlers. Returns a singleton array
// containing a node of the given type with all arguments sent through either
// parseNonmath (inMath false) or parseMath (inMath true).
// If psList is falsy, this returns false. Pass [] if there are no arguments
// and you don't want an error to be signalled.
const makeSimpleCommandNode = function(
	inMath,
	nodeType,
	psList,
	nodeOptions = {}
) {
	if (!psList) {
		return false;
	}
	const retval = [
		_.extend(
			{
				type: nodeType,
				children: psList.map(ps => makeSimpleArgNode(inMath, ps)),
			},
			nodeOptions
		),
	];
	return retval;
};
// Readable versions of makeSimpleCommandNode with inMath filled in already.
// NOTE: nonmath is not currently used.
const makeNonmathCommandNode = _.partial(makeSimpleCommandNode, false); // eslint-disable-line
const makeMathCommandNode = _.partial(makeSimpleCommandNode, true);

// Convenience function for command handlers. Returns a node of type either
// nonmath or math, whose children are the results of sending the provided
// content through either parseMath or parseNonmath depending on inMath's value.
const makeSimpleArgNode = function(inMath, ps, {noTrim} = {}) {
	const node = {
		type: inMath ? "math" : "nonmath",
		children: (inMath ? ps.parseMath : ps.parseNonmath)(ps),
	};
	return !noTrim ? trimNode(node) : node;
};
// Readable versions of makeSimpleArgNode with inMath filled in already.
const makeNonmathArgNode = _.partial(makeSimpleArgNode, false);
const makeMathArgNode = _.partial(makeSimpleArgNode, true);

// Convenience function for nonmath command handlers. Parses the provided
// content, then wraps all nonspace nodes in provided node type.
// Unlike makeSimpleCommandNode, only pass one parser.
const makeFormattedText = function(nodeType, ps, nodeOptions = {}) {
	const {hideBlanks} = nodeOptions;

	if (!ps) {
		return false;
	}
	return ps.parseNonmath(ps).map(function(node) {
		return isSpaceNode(node)
			? (hideBlanks && {type: "text", text: ""}) || node
			: _.extend(
					{
						type: nodeType,
						children: [node],
					},
					nodeOptions
			  );
	});
};

// Given a delimiter row, split the text up by it, and return an array of parser
// states each with the split text and the error logging set up properly. The
// length of the returned array is always one plus the number of found
// delimiters.
const splitByDelimIntoPs = function(ps, delimRow) {
	ps = PS.convert(ps);
	const allParts = DelimParser.splitByDelimSet(ps, [delimRow]);
	let pos = ps.pos;
	const psList = [];
	let lastText = "";
	for (let i = 0; i < allParts.length; i += 1) {
		if (allParts[i].type !== "text") {
			psList.push(ps.withText(lastText).pipeErrorsAt(pos));
			pos += lastText.length + allParts[i].text.length;
			lastText = "";
		} else {
			lastText += allParts[i].text;
		}
	}
	psList.push(ps.withText(lastText).pipeErrorsAt(pos));
	return psList;
};

// Augments findDelim to search for an ending delimiter where it is possible
// for delimiters to be nested, and inner ending delimiters should be skipped.
// Returns the position of the given delimiter type, or -1 if not found.
// The parser state is not modified; the calling code is responsible for
// generating an error if a needed delimiter is not found.
const findNestedDelim = function(ps, beginDelimRow, endDelimRow) {
	ps = PS.convert(ps);
	let delim;
	let nested = 1;
	let pos = ps.pos;
	const table = [beginDelimRow, endDelimRow];
	while (nested > 0) {
		delim = DelimParser.findNextDelim(ps.at(pos), table);
		if (!delim) {
			return -1;
		} else if (delim.block.begin === beginDelimRow.begin) {
			nested += 1;
			pos = delim.end;
		} else if (delim.block.begin === endDelimRow.begin) {
			nested -= 1;
			pos = delim.end;
		}
	}
	return delim ? delim.start : -1;
};

// Convenience wrapper around DelimParser for finding one delimiter type.
// Returns the position of the given delimiter type, or -1 if not found.
// The parser state is not modified; the calling code is responsible for
// generating an error if a needed delimiter is not found.
const findDelim = function(ps, delimRow) {
	ps = PS.convert(ps);
	const delim = DelimParser.findNextDelim(ps, [delimRow]);
	return delim ? delim.start : -1;
};

// Parse a string which may have a comma separated number into a Javascript
// number type.
const parseCommaNumber = function(numString) {
	numString = numString.replace(/[,\s]/g, "");
	return parseFloat(numString);
};

//Given a number (or a string of a number), return the string with commas in the correct locations
const withCommas = function(num) {
	//code from http://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
	const parts = num.toString().split(".");
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return parts.join(".");
};

// Given an array of nodes, take any adjacent text nodes and merge them into
// one node with concatenated content. Return the array resulting from all
// this merging.
const mergeTextNodes = function(nodeList) {
	const result = [];
	// If the last node was not text, this will be set to null.
	let lastTextNode = null;
	for (let i = 0; i < nodeList.length; i += 1) {
		if (lastTextNode && nodeList[i].type === "text") {
			lastTextNode.text += nodeList[i].text;
		} else if (nodeList[i].type === "text") {
			lastTextNode = {type: "text", text: nodeList[i].text};
			// lastTextNode may be intentionally mutated after this push.
			result.push(lastTextNode);
		} else {
			lastTextNode = null;
			result.push(nodeList[i]);
		}
	}
	return result;
};

// Given a node whose children may contain spaces at the beginning or end,
// remove those spaces. Mutates the passed node and returns it.
const trimNode = function(node) {
	// From the beginning
	while (isSpaceNode(node.children[0], {notManual: true})) {
		node.children = node.children.slice(1);
	}
	// From the end
	while (
		isSpaceNode(node.children[node.children.length - 1], {notManual: true})
	) {
		node.children = node.children.slice(0, -1);
	}
	return node;
};

// Turns first and last spaces into manual spaces.
const keepEdgeSpaces = function(node) {
	if (isSpaceNode(node.children[0])) {
		node.children[0].manual = true;
	}

	const lastChild = node.children[node.children.length - 1];
	if (isSpaceNode(lastChild)) {
		lastChild.manual = true;
	}
	return node;
};

// Return whether the node is spacing only.
const isSpaceNode = function(node, {notManual} = {}) {
	const nodeTypes = ["space", "lineBreak", "indent"];
	return (
		node && nodeTypes.indexOf(node.type) > -1 && !(notManual && node.manual)
	);
};

// Parse optional argument that begins with "color="
const parseColor = function(ps, optArg) {
	const color = optArg.substring("color=".length);
	if (!DiagramColors[color]) {
		ps.pushError(optArg + " is not a valid diagram color.");
	}
	return color;
};

// Find the first descendant of the given node, where predicate(descendant) is true
// "First" is defined as the earliest node found in a preorder traversal.
const findFirstDescendant = function(node, predicate, includeRoot = false) {
	if (includeRoot && predicate(node)) {
		return node;
	}
	let match;
	node.children &&
		node.children.forEach(child => {
			if (!match) {
				match = findFirstDescendant(child, predicate, true);
			}
		});
	return match;
};

// Get the given batex string without any surrounding "$". Used for when
// we want to re-wrap a command as an argument for another command.
const getInnerBatex = function(batexStr) {
	const batexPattern = /^\$(.+)\$$/g;
	const match = batexPattern.exec(batexStr);
	return match[1];
};

// Parse multiple, optional arguments of the form
// \command[name=value]
const parseNamedOptionalArgs = function(ps, options) {
	let optArg;
	const allPossibleKeys = _.keys(options);
	const allPossibleKeysMessage = `${allPossibleKeys
		.map(k => `"${k}"`)
		.join(", ")}`;

	while (true) {
		optArg = findOptionalArgument(ps);
		if (!optArg) {
			break;
		}

		const argStr = optArg ? optArg.getRest() : "";
		const [key, val] = argStr.split("=");
		if (!allPossibleKeys.includes(key)) {
			ps.pushError(
				`"${key}" is not a valid option. Must be one of ${allPossibleKeysMessage}`
			);
			return false;
		}
		options[key] = val;
	}
	return options;
};

module.exports = {
	parseAndIgnoreSpace,
	checkForFourDigits,
	findArguments,
	findSomeArguments,
	findOptionalArgument,
	findOptionalArguments,
	splitArgIntoText,
	splitArgIntoNumbers,
	splitArgIntoMathNodes,
	splitArgIntoNonmathNodes,
	parseStartOfMath,
	makeSimpleCommandNode,
	makeNonmathCommandNode,
	makeMathCommandNode,
	makeSimpleArgNode,
	makeNonmathArgNode,
	makeMathArgNode,
	makeFormattedText,
	splitByDelimIntoPs,
	findNestedDelim,
	findDelim,
	parseCommaNumber,
	withCommas,
	mergeTextNodes,
	isSpaceNode,
	trimNode,
	keepEdgeSpaces,
	parseColor,
	findFirstDescendant,
	getInnerBatex,
	parseNamedOptionalArgs,
};
